import { useState, useEffect } from 'react';
import './Admin.css';

function Admin() {
    const [members, setMembers] = useState([]);
    const [error, setError] = useState(null);
    const [selectedMembers, setSelectedMembers] = useState(new Set());
    const [isAddingRandom, setIsAddingRandom] = useState(false);
    const [isKickingAll, setIsKickingAll] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const [serverStatus, setServerStatus] = useState('checking');
    const [raffleStatus, setRaffleStatus] = useState({ active: false, matches: [] });
    const [isStartingRaffle, setIsStartingRaffle] = useState(false);
    const [isEndingRaffle, setIsEndingRaffle] = useState(false);

    useEffect(() => {
        // Check server status first
        checkServerStatus();

        // Then fetch members and set up polling
        fetchMembers();
        const interval = setInterval(fetchMembers, 5000);
        return () => clearInterval(interval);
    }, []);

    // Check raffle status periodically
    useEffect(() => {
        const checkRaffleStatus = async () => {
            try {
                const response = await fetch('/api/raffle/status');
                const data = await response.json();
                setRaffleStatus(data);
            } catch (err) {
                console.error('Failed to fetch raffle status:', err);
            }
        };

        checkRaffleStatus();
        const interval = setInterval(checkRaffleStatus, 5000);
        return () => clearInterval(interval);
    }, []);

    // Check if server is reachable
    const checkServerStatus = async () => {
        try {
            const response = await fetch('/api/members', {
                method: 'GET',
                signal: AbortSignal.timeout(3000)
            });
            setServerStatus(response.ok ? 'online' : 'error');
        } catch (err) {
            console.error('Server status check failed:', err);
            setServerStatus('offline');
            setError('Cannot connect to server. Please make sure the backend is running.');
        }
    };

    // Clear success message after 3 seconds
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage(null);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    const fetchMembers = async () => {
        try {
            const response = await fetch('/api/members');
            const data = await response.json();
            setMembers(data.members);
        } catch (err) {
            console.error('Failed to fetch members:', err);
            setError('Failed to load party members');
        }
    };

    const handleKickSelected = async () => {
        if (selectedMembers.size === 0) return;

        try {
            const response = await fetch('/api/admin/kick', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ members: Array.from(selectedMembers) }),
            });

            if (response.ok) {
                setSelectedMembers(new Set());
                fetchMembers();
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to kick members');
            }
        } catch (err) {
            console.error('Failed to kick members:', err);
            setError('Failed to kick members');
        }
    };

    const handleAddRandom = async () => {
        setIsAddingRandom(true);
        setError(null);

        try {
            console.log('Adding random members...');
            const response = await fetch('/api/admin/add-random', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });

            const data = await response.json();

            if (response.ok) {
                console.log('Successfully added random members:', data);
                fetchMembers();
            } else {
                console.error('Error response from server:', data);
                setError(data.error || 'Failed to add random members');
            }
        } catch (err) {
            console.error('Failed to add random members:', err);
            setError('Network error: Failed to add random members');
        } finally {
            setIsAddingRandom(false);
        }
    };

    const handleKickAll = async () => {
        // Confirm before proceeding
        if (!window.confirm('Are you sure you want to kick ALL members? This action cannot be undone.')) {
            return;
        }

        setIsKickingAll(true);
        setError(null);

        // Retry logic - try up to 3 times
        for (let attempt = 0; attempt < 3; attempt++) {
            try {
                console.log(`Attempting to kick all members (attempt ${attempt + 1}/3)...`);

                const response = await fetch('/api/admin/kick-all', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Cache-Control': 'no-cache'
                    },
                    // Add timeout to prevent hanging requests
                    signal: AbortSignal.timeout(5000)
                });

                const data = await response.json();

                if (response.ok && data.success) {
                    setSuccessMessage(`Successfully kicked all ${data.count} members`);
                    setSelectedMembers(new Set());
                    fetchMembers();
                    setIsKickingAll(false);
                    return; // Success! Exit the function
                } else {
                    throw new Error(data.error || 'Server error');
                }
            } catch (err) {
                console.error(`Attempt ${attempt + 1} failed:`, err);

                // If this is the last attempt, show error to user
                if (attempt === 2) {
                    setError(`Server connection error. Make sure the server is running on port 3000.`);
                } else {
                    // Wait before trying again (exponential backoff)
                    await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
                }
            }
        }

        setIsKickingAll(false);
    };

    const handleStartRaffle = async () => {
        setIsStartingRaffle(true);
        setError(null);

        try {
            const response = await fetch('/api/raffle/start', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });

            const data = await response.json();

            if (response.ok) {
                setSuccessMessage('Raffle started successfully!');
                setRaffleStatus({ active: true, matches: data.matches });
            } else {
                setError(data.error || 'Failed to start raffle');
            }
        } catch (err) {
            console.error('Failed to start raffle:', err);
            setError('Failed to start raffle');
        } finally {
            setIsStartingRaffle(false);
        }
    };

    const handleEndRaffle = async () => {
        setIsEndingRaffle(true);
        setError(null);

        try {
            const response = await fetch('/api/raffle/end', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });

            const data = await response.json();

            if (response.ok) {
                setSuccessMessage('Raffle ended successfully!');
                setRaffleStatus({ active: false, matches: [] });
            } else {
                setError(data.error || 'Failed to end raffle');
            }
        } catch (err) {
            console.error('Failed to end raffle:', err);
            setError('Failed to end raffle');
        } finally {
            setIsEndingRaffle(false);
        }
    };

    const toggleMemberSelection = (memberName) => {
        const newSelected = new Set(selectedMembers);
        if (newSelected.has(memberName)) {
            newSelected.delete(memberName);
        } else {
            newSelected.add(memberName);
        }
        setSelectedMembers(newSelected);
    };

    return (
        <div className="admin-page">
            <div className="admin-header">
                <h1>BBQ Admin Panel</h1>
                <div className={`server-status ${serverStatus}`}>
                    Server: {serverStatus === 'checking' ? 'Checking...' :
                        serverStatus === 'online' ? 'Online' :
                            serverStatus === 'offline' ? 'Offline' : 'Error'}
                </div>
            </div>

            <div className="admin-content">
                {successMessage && (
                    <div className="success-message">
                        {successMessage}
                    </div>
                )}

                <div className="admin-section">
                    <h2>Raffle Control</h2>
                    <div className="raffle-status">
                        Status: <span className={raffleStatus.active ? 'active' : 'inactive'}>
                            {raffleStatus.active ? 'Active' : 'Inactive'}
                        </span>
                    </div>
                    <div className="admin-buttons">
                        <button
                            onClick={handleStartRaffle}
                            disabled={isStartingRaffle || raffleStatus.active || members.length < 2}
                            className="start-raffle-button"
                        >
                            {isStartingRaffle ? 'Starting...' : 'Start Raffle'}
                        </button>
                        <button
                            onClick={handleEndRaffle}
                            disabled={isEndingRaffle || !raffleStatus.active}
                            className="end-raffle-button"
                        >
                            {isEndingRaffle ? 'Ending...' : 'End Raffle'}
                        </button>
                    </div>
                    {raffleStatus.active && raffleStatus.matches && (
                        <div className="raffle-results">
                            <h3>Current Raffle Tasks</h3>
                            <div className="matches-list">
                                {raffleStatus.matches.map((match, index) => (
                                    <div key={index} className="match-item">
                                        <h4>Task: {match.task.task_text}</h4>
                                        <p>Participants: {match.group.join(', ')}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="admin-controls">
                    <h2>Party Members ({members.length})</h2>
                    <div className="admin-buttons">
                        <button
                            onClick={handleKickAll}
                            disabled={isKickingAll || members.length === 0}
                            className="kick-all-button"
                        >
                            {isKickingAll ? 'Processing...' : 'Kick All Members'}
                        </button>
                        <button
                            onClick={handleKickSelected}
                            disabled={selectedMembers.size === 0}
                            className="kick-button"
                        >
                            Kick Selected ({selectedMembers.size})
                        </button>
                        <button
                            onClick={handleAddRandom}
                            disabled={isAddingRandom}
                            className="add-random-button"
                        >
                            {isAddingRandom ? 'Adding...' : 'Add 15 Random Names'}
                        </button>
                    </div>
                </div>

                {error && <p className="error-message">{error}</p>}

                <div className="members-list">
                    {members.map((memberName) => (
                        <div
                            key={memberName}
                            className={`member-item ${selectedMembers.has(memberName) ? 'selected' : ''}`}
                            onClick={() => toggleMemberSelection(memberName)}
                        >
                            <span className="member-name">{memberName}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default Admin; 